import { Directive, inject, input, TemplateRef } from '@angular/core';

/** Context passed to a `whColumn` cell template. `$implicit`/`row` are `any` so cell templates
 *  read `row.foo` ergonomically (same as the integrator's untyped `let-site` cells) — the row
 *  shape isn't known to the generic table at the template level. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface WhColumnContext<T = any> {
  $implicit: T;
  row: T;
  index: number;
}

/**
 * Declares one column of a `<wh-table>`. Put it on an `<ng-template>` inside the table:
 *
 *   <ng-template whColumn header="Name" key="name" [sortable]="true" let-row let-i="index">
 *     <div class="font-semibold">{{ row.name }}</div>
 *   </ng-template>
 *
 * The cell content is a real template so it can render anything (pills, badges, buttons,
 * multi-line cells) — not just a text field.
 */
@Directive({
  selector: '[whColumn]',
  standalone: true,
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class WhColumnDirective<T = any> {
  /** Column header label. */
  readonly header = input<string>('');
  /** Sort key / stable id. Required for a sortable column; also used as the `@for` track. */
  // No alias: with `alias: 'whColumn'` the public input name was `whColumn`, so the bare
  // `whColumn` attribute every caller writes bound key to '' and the documented `key="name"`
  // was an inert HTML attribute. `col.key()` was always falsy, so wh-table never rendered a
  // sort button on any column, in either app.
  readonly key = input<string | undefined>(undefined);
  /** Whether the header is a tri-state sort button. */
  readonly sortable = input<boolean>(false);
  /** Text alignment of the cell + header. */
  readonly align = input<'left' | 'center' | 'right'>('left');
  /** Optional explicit grid track (e.g. `'70px'`, `'minmax(180px,2fr)'`). Falls back to `1fr`. */
  readonly width = input<string | undefined>(undefined);

  readonly tpl = inject<TemplateRef<WhColumnContext<T>>>(TemplateRef);

  /** Lets `let-row`/`let-i="index"` type-check in templates. */
  static ngTemplateContextGuard<T>(_dir: WhColumnDirective<T>, _ctx: unknown): _ctx is WhColumnContext<T> {
    return true;
  }
}

/**
 * Optional custom empty state: `<ng-template whTableEmpty>No sites found</ng-template>`.
 * Falls back to a default "No data" message when absent.
 */
@Directive({
  selector: '[whTableEmpty]',
  standalone: true,
})
export class WhTableEmptyDirective {
  readonly tpl = inject<TemplateRef<unknown>>(TemplateRef);
}
